































































































































































































































import { defineComponent, PropType, computed } from "@vue/composition-api";
import OccModal from "@/components/Atoms/OccModal/index.vue";
import OccButton from "@/components/Atoms/OccButton/index.vue";
import OccHeading from "@/components/Atoms/OccHeading/index.vue";
import ListTable from "@/components/Molecules/ListTable/index.vue"; // Props as TableProps
import { ApiRequest, ApiResponse, EditingJobEventSchedule } from "@/api/types";
import { occWhite } from "@/assets/styles/vars-for-script.scss";
import dayjs from "dayjs";
import { useGlobalState } from "@/helpers/hooks/useGlobalState";
import { JOB_EVENT_STORE_KEY } from "@/helpers/hooks/useJobEvent";
import { isNotNullOrUndefined } from "@/helpers/type-helper";
import OccAlert from "@/components/Molecules/OccAlert/index.vue";
import HelpLink from "@/components/Atoms/HelpLink/index.vue";

export default defineComponent({
  name: "PublishConfirmModal",
  props: {
    jobEvent: {
      type: Object as PropType<ApiRequest.EditingDraftJobEvent>,
      default: false
    },
    contracts: {
      type: Array as PropType<ApiResponse.ContractInformation[]>,
      default: () => []
    },
    gaEventCategory: {
      type: String,
      default: ""
    }
  },
  components: {
    OccAlert,
    OccModal,
    OccButton,
    OccHeading,
    ListTable,
    HelpLink
  },
  emits: ["confirm", "close"],
  setup(props) {
    const { defaultMailTemplates } = useGlobalState(JOB_EVENT_STORE_KEY);

    const tableRows = computed(() => {
      return [
        {
          title: "対象契約（卒年度）",
          contract: props.jobEvent.contract_id
            ? contractTitle(props.jobEvent.contract_id)
            : undefined
        },
        props.jobEvent.iweb_graduate_year && {
          title: "i-web連携卒年度",
          iweb_graduate_year: props.jobEvent.iweb_graduate_year
            ? props.jobEvent.iweb_graduate_year
            : undefined
        },
        { title: "抽選の有無", is_lottery: props.jobEvent.is_lottery },
        {
          title:
            props.jobEvent.public_status === "public"
              ? "更新希望日時"
              : "公開希望日時",
          preferred_publish_at: props.jobEvent.preferred_publish_at,
          scheduled_publish: props.jobEvent.scheduled_publish
        },
        { title: "スケジュール", schedules: props.jobEvent.schedules },
        (props.jobEvent.reception_type === "internal" || undefined) && {
          title: "自動返信メール",
          mailNotifications: props.jobEvent.mail_notifications,
          externalLink: {
            text: "自動返信メールの設定",
            url: `/job/events/${props.jobEvent.event_id}/schedules`
          }
        }
      ].filter(isNotNullOrUndefined);
    });

    const formatDate = (at: string) =>
      dayjs(new Date(at)).format("YYYY/MM/DD HH:mm");

    const getDisplayScheduleSpan = (start?: string, end?: string) => {
      if (start && end) {
        return `${formatDate(start)} 〜 ${formatDate(end)}`;
      } else if (start) {
        return formatDate(start) + " 〜";
      } else if (end) {
        return "〜 " + formatDate(end);
      } else {
        return "";
      }
    };

    const ALERT_MESSAGE_NOT_SEND =
      "メール文面が編集されていますが、送信しない設定になっています。送信設定に間違いがないかご確認ください。";
    const ALERT_MESSAGE_NOT_START =
      "メール文面が編集されていますが、イベント開催日程の入力がないスケジュールではメールが送信されません。";
    const ALERT_MESSAGE_IWEB_GRADUATE_YEAR_MISMATCH =
      "対象契約とi-web連携の卒年度が一致していません。";

    const isShowNotSendAlert = computed(() => {
      return props.jobEvent.schedules.some(schedule => {
        return existsNotSendChangedMailTemplate(schedule);
      });
    });

    const existsNotSendChangedMailTemplate = (
      schedule: EditingJobEventSchedule
    ) => {
      const targetMailTemplateKeys = [
        "reminder",
        "three_days_before_reminder",
        "seven_days_before_reminder",
        "thanks"
      ] as const;

      return targetMailTemplateKeys.some(key => {
        if (!schedule.mail_template) {
          return false;
        }

        const editingSchedulesMailTemplate = schedule.mail_template[key];
        return (
          editingSchedulesMailTemplate &&
          editingSchedulesMailTemplate !== "" &&
          editingSchedulesMailTemplate.replace(/\r/g, "") !==
            defaultMailTemplates.find(template => template.key === key)
              ?.default &&
          props.jobEvent.mail_notifications[key] === false
        );
      });
    };

    const isShowNotStartAlert = computed(() => {
      // イベント開催日程未設定アラートは未承認スケジュールのみを対象として判定する
      return props.jobEvent.schedules
        .filter(schedule => !schedule.schedule_id)
        .some(schedule => {
          return existsChangedMailTemplateAndNoStart(schedule);
        });
    });

    const existsChangedMailTemplateAndNoStart = (
      schedule: EditingJobEventSchedule
    ) => {
      const targetMailTemplateKeys = [
        "reminder",
        "three_days_before_reminder",
        "seven_days_before_reminder",
        "thanks"
      ] as const;

      return targetMailTemplateKeys.some(key => {
        if (!schedule.mail_template) {
          return false;
        }

        const editingSchedulesMailTemplate = schedule.mail_template[key];
        return (
          editingSchedulesMailTemplate &&
          editingSchedulesMailTemplate !== "" &&
          editingSchedulesMailTemplate.replace(/\r/g, "") !==
            defaultMailTemplates.find(template => template.key === key)
              ?.default &&
          !schedule.start_at
        );
      });
    };

    const contractTitle = (contractId: number) => {
      const contract = props.contracts.find(
        contract => contract.id === contractId
      );
      return contract
        ? `【${contract.graduate_year}年卒】${contract.plan}（${contract.period}）`
        : undefined;
    };

    const isShowIwebGraduateYearMismatchAlert = computed(() => {
      if (!props.jobEvent.iweb_graduate_year) {
        return false;
      }

      const contract = props.contracts.find(
        contract => contract.id === props.jobEvent.contract_id
      );
      const contractGraduateYear = contract ? contract?.graduate_year : null;

      return (
        String(contractGraduateYear) !==
        String(props.jobEvent.iweb_graduate_year)
      );
    });

    return {
      occWhite,
      tableRows,
      formatDate,
      getDisplayScheduleSpan,
      isShowNotSendAlert,
      isShowNotStartAlert,
      isShowIwebGraduateYearMismatchAlert,
      ALERT_MESSAGE_NOT_SEND,
      ALERT_MESSAGE_NOT_START,
      ALERT_MESSAGE_IWEB_GRADUATE_YEAR_MISMATCH
    };
  }
});
